import { DayTimeDuration } from '@ais-ui/ui-core';

export function formatCustomDuration(
  duration: string | DayTimeDuration | undefined,
): string {
  if (!duration) {
    return 'P0000-00-00T00:00:00';
  }

  // Handle new object format { days, hours, minutes }
  if (typeof duration === 'object' && 'days' in duration) {
    const paddedDays = String(duration.days).padStart(2, '0');
    const paddedHours = String(duration.hours).padStart(2, '0');
    const paddedMinutes = String(duration.minutes).padStart(2, '0');
    return `P0000-00-${paddedDays}T${paddedHours}:${paddedMinutes}:00`;
  }

  // Handle legacy string format: "5 10:00"
  const parts = duration.split(' ');
  if (parts.length !== 2) {
    throw new Error('Invalid duration format');
  }

  const [days, time] = parts;
  const [hours, minutes] = time.split(':').map(Number);

  if (isNaN(hours) || isNaN(minutes)) {
    throw new Error('Invalid time format');
  }

  // Zero-padding for hours, minutes, and seconds
  const paddedHours = String(hours).padStart(2, '0');
  const paddedMinutes = String(minutes).padStart(2, '0');
  const paddedDays = String(days).padStart(2, '0');

  // Constructing the custom duration string
  return `P0000-00-${paddedDays}T${paddedHours}:${paddedMinutes}:00`;
}

export function parseCustomDuration(
  customDuration: string,
): undefined | DayTimeDuration {
  // Accepts both the app's canonical/alternative ISO-8601 form
  // (e.g. "P0000-00-05T10:30:00") and standard ISO-8601 durations
  // (e.g. "PT1H", "P1DT2H30M", "P0Y0M5DT10H0M0S", "P1W"). Whatever the backend
  // serialized, we resolve it to whole seconds and normalize into a
  // {days, hours, minutes} value so the duration survives a clone round-trip.
  if (typeof customDuration !== 'string') {
    return;
  }

  const trimmed = customDuration.trim();
  if (!trimmed) {
    return;
  }

  const totalSeconds =
    parseAlternativeIsoDuration(trimmed) ??
    parseStandardIsoDuration(trimmed) ??
    parseTimeDuration(trimmed);

  if (totalSeconds === undefined) {
    return;
  }

  return secondsToDayTimeDuration(totalSeconds);
}

/**
 * Reads a report's max duration from backend job params. The backend
 * normalizes MDUR into `max_duration` ("HHHH:MM:SS"), and for some job types
 * (e.g. port trips) re-inserts a "0:00:00" default under MDUR, so a
 * non-positive MDUR falls back to `max_duration`.
 */
export function parseMaxDurationParam(
  params: Record<string, unknown> | undefined,
): undefined | DayTimeDuration {
  const mdur = parseCustomDuration(params?.['MDUR'] as string);
  if (mdur && (mdur.days > 0 || mdur.hours > 0 || mdur.minutes > 0)) {
    return mdur;
  }

  return parseCustomDuration(params?.['max_duration'] as string) ?? mdur;
}

// Backend time form: HHHH:MM[:SS], where hours may exceed 24.
const TIME_DURATION_REGEX = /^(\d+):(\d{1,2})(?::(\d{1,2})(?:\.\d+)?)?$/;

function parseTimeDuration(value: string): number | undefined {
  const match = value.match(TIME_DURATION_REGEX);
  if (!match) {
    return;
  }

  const [, hours, minutes, seconds] = match;

  return Number(hours) * 3600 + Number(minutes) * 60 + Number(seconds ?? 0);
}

// Alternative ISO-8601 duration form: P[YYYY]-[MM]-[DD]T[hh]:[mm][:[ss]]
// Years and months are always zero for these durations and are ignored.
const ALTERNATIVE_ISO_DURATION_REGEX =
  /^P(\d{1,4})-(\d{1,2})-(\d{1,2})T(\d{1,2}):(\d{1,2})(?::(\d{1,2})(?:\.\d+)?)?$/;

function parseAlternativeIsoDuration(value: string): number | undefined {
  const match = value.match(ALTERNATIVE_ISO_DURATION_REGEX);
  if (!match) {
    return;
  }

  const [, , , days, hours, minutes, seconds] = match;

  return (
    Number(days) * 86400 +
    Number(hours) * 3600 +
    Number(minutes) * 60 +
    Number(seconds ?? 0)
  );
}

// Standard ISO-8601 duration form: PnYnMnWnDTnHnMnS.
// Years and months can't be converted to an exact number of days, so they are
// ignored (they are always zero for report durations).
const STANDARD_ISO_DURATION_REGEX =
  /^P(?:\d+Y)?(?:\d+M)?(?:(\d+)W)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)(?:\.\d+)?S)?)?$/;

function parseStandardIsoDuration(value: string): number | undefined {
  const match = value.match(STANDARD_ISO_DURATION_REGEX);
  if (!match) {
    return;
  }

  const [, weeks, days, hours, minutes, seconds] = match;

  // Reject empty durations such as "P" or "PT" that carry no time component.
  if (
    weeks === undefined &&
    days === undefined &&
    hours === undefined &&
    minutes === undefined &&
    seconds === undefined
  ) {
    return;
  }

  return (
    Number(weeks ?? 0) * 604800 +
    Number(days ?? 0) * 86400 +
    Number(hours ?? 0) * 3600 +
    Number(minutes ?? 0) * 60 +
    Number(seconds ?? 0)
  );
}

function secondsToDayTimeDuration(totalSeconds: number): DayTimeDuration {
  const totalMinutes = Math.floor(totalSeconds / 60);

  return {
    days: Math.floor(totalMinutes / (24 * 60)),
    hours: Math.floor((totalMinutes % (24 * 60)) / 60),
    minutes: totalMinutes % 60,
  };
}

export function parseSecondsToCustomDuration(
  seconds: number,
): undefined | string {
  if (typeof seconds !== 'number' || isNaN(seconds) || seconds < 0) {
    return;
  }

  const days = Math.floor(seconds / (3600 * 24));
  const hours = Math.floor((seconds % (3600 * 24)) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  let result = '';

  if (days > 0) {
    result += `${days} day${days > 1 ? 's' : ''} `;
  }
  if (hours > 0) {
    result += `${hours} hour${hours > 1 ? 's' : ''} `;
  }
  if (minutes > 0 || (days === 0 && hours === 0)) {
    result += `${minutes} minute${minutes > 1 ? 's' : ''}`;
  }

  return result.trim();
}
